import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const MINIMUM_ROUTE_LOADING_MS = 500;

export function GlobalLoadingIndicator() {
  const location = useLocation();
  const isFetching = useIsFetching({
    predicate: (query) => query.state.fetchStatus === "fetching" && query.state.dataUpdatedAt === 0,
  });
  const isMutating = useIsMutating();
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    setIsRouteLoading(true);
    const timeoutId = window.setTimeout(() => {
      setIsRouteLoading(false);
    }, MINIMUM_ROUTE_LOADING_MS);

    return () => window.clearTimeout(timeoutId);
  }, [location.key]);

  const isVisible = useMemo(
    () => isRouteLoading || isFetching > 0 || isMutating > 0,
    [isFetching, isMutating, isRouteLoading]
  );

  return (
    <div
      className={cn(
        "pointer-events-none fixed left-0 top-0 z-[60] h-1 w-full overflow-hidden transition-opacity duration-200",
        isVisible ? "opacity-100" : "opacity-0"
      )}
      aria-hidden={!isVisible}
    >
      <div className="h-full w-1/2 animate-route-loading bg-gradient-to-r from-transparent via-primary to-transparent" />
      <span className="sr-only">Loading</span>
    </div>
  );
}
